import Add16 from '@carbon/icons-react/es/add/16';
import SubtractAlt20 from '@carbon/icons-react/es/subtract--alt/20';
import { Col, Row, Tooltip } from 'antd';
import { FormListFieldData, FormListOperation } from 'antd/es/form/FormList';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  FormTemplateAutomationRuleOption,
  FormTemplateTaskCreationConfig,
  ProjectTemplatePhase,
} from 'api/form';
import { Button } from 'components/atoms/button';
import { Drawer as DrawerAtom } from 'components/atoms/drawer/drawer';
import { Form } from 'components/atoms/form';
import { Select } from 'components/atoms/select';
import {
  Label01,
  ProductiveHeading05,
  Text01,
  Text02,
} from 'components/styles';
import { useTranslation } from 'hooks/use-translation';

const Drawer = styled(DrawerAtom).attrs({
  width: 648,
  closable: true,
  placement: 'right',
})`
  .ant-drawer-body {
    padding: 0;
    overflow: hidden;
  }
`;

const Header = styled.div`
  background: var(--task-pane-header-bg);
  padding: var(--spacing-05) var(--spacing-07);
  height: 88px;
  align-items: flex-end;
  display: flex;
`;
const Content = styled.div`
  padding: var(--spacing-08) var(--spacing-06) 150px;
  height: 100%;
  overflow-y: scroll;
`;

const HeaderTitle = styled.div`
  ${ProductiveHeading05};
  ${Text01};
`;
const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-03);
`;
const FormItem = styled(Form.Item)`
  && {
    margin-bottom: var(--layout-01);
  }
`;
const FieldLabel = styled.div`
  ${Label01};
  ${Text02};
  color: var(--gray-80);
  padding-bottom: var(--spacing-02);
`;
const DeleteButtonCol = styled(Col)`
  align-self: center;
  top: var(--spacing-03);
  flex: 0 0 40px;

  ${Button} {
    color: var(--gray-50);
  }
`;
const AddNewConditionButton = styled(Button)`
  padding: 0px;
`;

const FormAutomationItem = ({
  field,
  fieldIndex,
  remove,
  formValues,
  fieldValue,
  projectPhaseMapping = [],
}: {
  field: FormListFieldData;
  fieldIndex: number;
  remove: (index: number | number[]) => void;
  formValues: any;
  fieldValue: any;
  projectPhaseMapping: ProjectTemplatePhase[];
}) => {
  const requiredMessage = useTranslation('input.required');
  const selectInputRef = useRef<any>(null);
  const ProjectOptions = useMemo(() => {
    const selectedProjectIds = (formValues.ruleList || []).map((mapping) => {
      return mapping.projectTemplateId ? mapping.projectTemplateId.value : null;
    });

    const optionsList = [] as { label: string; value: number }[];

    projectPhaseMapping.forEach((option) => {
      if (
        fieldValue.projectTemplateId &&
        fieldValue.projectTemplateId.value === option.projectTemplateId
      ) {
        optionsList.push({
          label: option.projectTemplateName,
          value: option.projectTemplateId,
        });
      } else if (!selectedProjectIds.includes(option.projectTemplateId)) {
        optionsList.push({
          label: option.projectTemplateName,
          value: option.projectTemplateId,
        });
      }
    });

    return optionsList;
  }, [fieldValue.projectTemplateId, formValues.ruleList, projectPhaseMapping]);

  const ProjectPhaseOptions = useMemo(() => {
    if (fieldValue?.projectTemplateId?.value) {
      const selectedProjectTemplate = projectPhaseMapping.find(
        (project) =>
          project.projectTemplateId === fieldValue.projectTemplateId.value,
      );

      return selectedProjectTemplate
        ? selectedProjectTemplate.projectPhases.map((phase) => {
            return {
              label: phase.phaseTemplateName,
              value: phase.phaseTemplateId,
            };
          })
        : [];
    }

    return [];
  }, [fieldValue?.projectTemplateId?.value, projectPhaseMapping]);

  useEffect(() => {
    if (
      fieldValue?.phaseTemplateId &&
      !ProjectPhaseOptions.find(
        (phase) => phase.value === fieldValue?.phaseTemplateId.value,
      )
    ) {
      selectInputRef?.current?.select?.clearValue();
    }
  }, [ProjectPhaseOptions, fieldValue?.phaseTemplateId]);

  return (
    <Row gutter={8}>
      <Col flex="280px">
        <FieldLabel>
          <FormattedMessage id="form_template.automation.project_config_heading" />
        </FieldLabel>
        <FormItem
          {...field}
          name={[field.name, 'projectTemplateId']}
          fieldKey={[field.fieldKey, 'projectTemplateId']}
          rules={[
            {
              required: true,
              message: requiredMessage,
            },
          ]}
        >
          <Select isClearable options={ProjectOptions} />
        </FormItem>
      </Col>
      <Col flex="280px">
        <FieldLabel>
          <FormattedMessage id="form_template.automation.phase_config_heading" />
        </FieldLabel>

        <Form.Item
          noStyle
          shouldUpdate={(prev, current) => {
            return prev.exportFormat !== current.exportFormat;
          }}
        >
          <FormItem
            {...field}
            name={[field.name, 'phaseTemplateId']}
            fieldKey={[field.fieldKey, 'phaseTemplateId']}
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
            ]}
          >
            <Select
              ref={selectInputRef}
              isClearable
              options={ProjectPhaseOptions}
              isDisabled={!fieldValue.projectTemplateId}
            />
          </FormItem>
        </Form.Item>
      </Col>

      {fieldIndex !== 0 ? (
        <DeleteButtonCol>
          <Form.Item shouldUpdate>
            <Tooltip
              mouseEnterDelay={1}
              title={<FormattedMessage id="settings.fields.remove_option" />}
            >
              <Button
                size="small"
                type="text"
                icon={<SubtractAlt20 />}
                onClick={() => remove(field.name)}
              />
            </Tooltip>
          </Form.Item>
        </DeleteButtonCol>
      ) : null}
    </Row>
  );
};

export const FormAutomationConfigurationSlider = ({
  defaultRuleValue,
  showSlider,
  saveConfiguration,
  isSaveConfigurationLoading,
  closeSlider,
  projectPhaseMapping,
}: {
  defaultRuleValue:
    | { ruleList: FormTemplateAutomationRuleOption[] }
    | { ruleList: { projectTemplateId: null; phaseTemplateId: null }[] };
  showSlider: boolean;
  saveConfiguration: (values: FormTemplateTaskCreationConfig[]) => void;
  isSaveConfigurationLoading: boolean;
  closeSlider: () => void;
  projectPhaseMapping: ProjectTemplatePhase[];
}): JSX.Element => {
  const [form] = Form.useForm();

  const [formData, setFormData] = useState(defaultRuleValue);
  const submit = () => {
    const updatedValue = form
      .getFieldsValue()
      .ruleList.reduce((acc, mapping) => {
        if (!mapping.projectTemplateId) {
          return acc;
        }

        return [
          ...acc,
          {
            projectTemplateId: mapping.projectTemplateId.value,
            phaseTemplateId: mapping.phaseTemplateId.value,
          },
        ];
      }, []);

    saveConfiguration(updatedValue);
  };

  const handleValuesChange = (values, allValues) => {
    setFormData(allValues);
  };

  const onCloseSlider = () => {
    setFormData(defaultRuleValue);
    closeSlider();
  };

  return (
    <Drawer
      visible={showSlider}
      onClose={onCloseSlider}
      destroyOnClose
      footer={
        <ButtonGroup>
          <Button onClick={onCloseSlider}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            type="primary"
            onClick={submit}
            loading={isSaveConfigurationLoading}
          >
            <FormattedMessage id="save" />
          </Button>
        </ButtonGroup>
      }
    >
      <Header>
        <HeaderTitle>
          <FormattedMessage id="configure" />
        </HeaderTitle>
      </Header>

      <Content>
        <Form
          form={form}
          name="new_field_form"
          autoComplete="off"
          preserve={false}
          layout="vertical"
          initialValues={formData}
          onValuesChange={handleValuesChange}
        >
          <Form.Item shouldUpdate>
            <div>
              <Form.List name={['ruleList']}>
                {(fields, { add: addField, remove }: FormListOperation) => {
                  return (
                    <>
                      {fields.map((field, index) => {
                        const fieldValue = form.getFieldValue([
                          'ruleList',
                          field.name,
                        ]);

                        return (
                          <FormAutomationItem
                            fieldIndex={index}
                            field={field}
                            remove={remove}
                            formValues={formData}
                            fieldValue={fieldValue}
                            projectPhaseMapping={projectPhaseMapping}
                          />
                        );
                      })}
                      {fields.length < projectPhaseMapping.length ? (
                        <AddNewConditionButton
                          type="link"
                          icon={<Add16 />}
                          iconPlacement="right"
                          onClick={() =>
                            addField({
                              projectTemplateId: null,
                              phaseTemplateId: null,
                            })
                          }
                        >
                          <FormattedMessage id="form_template.automation.add_another_condition" />
                        </AddNewConditionButton>
                      ) : null}
                    </>
                  );
                }}
              </Form.List>
            </div>
          </Form.Item>
        </Form>
      </Content>
    </Drawer>
  );
};
